import type { HttpErrorResponse } from '@angular/common/http';

import type { ID } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  ngrxEvent,
  pipeState,
  scopeFactory,
} from '@core/ngrx-legacy';
import {
  AutomationTemplate,
  WorkflowAutomation,
  WorkflowAutomationAdditionalData,
  WorkflowAutomationCreatePayload,
  WorkflowAutomationOptionsData,
  WorkflowAutomationUpdatePayload,
} from '@core/workflow-automations-types';

export const jobTemplateIdScope = (jobTemplateId: number) =>
  scopeFactory('jobTemplateIdScope', jobTemplateId);

const idSelector = ({ id }: { id: number }) => id;
const { actions, namespace, reducer, selectors } =
  createEntityCollectionStore<WorkflowAutomation>()('JobTemplateWorkflowAutomations', {
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);

      return {
        fetchJobTemplateAutomations: (state, payload: { jobTemplateId: number; scope: string }) =>
          adapter.markAsPending(state, { scope: null, entities: [payload.jobTemplateId] }),
        fetchJobTemplateAutomationsSuccess: (state, { data, scope }) =>
          pipeState(
            state,
            next => adapter.setMany(next, data, { scope }),
            next =>
              adapter.markAsLoaded(next, { scope, entities: data.map(({ id }: { id: ID }) => id) }),
          ),
        fetchJobTemplateAutomationsFailed: defaultActions.fetchOneFailed,
        createOnePessimistic: (
          state,
          payload: {
            jobTemplateId: number;
            data: WorkflowAutomationCreatePayload;
            scope: string;
            correlationId: string;
          },
        ) => adapter.markAsPending(state),
        updateOnePessimistic: (
          state,
          payload: {
            jobTemplateId: number;
            data: WorkflowAutomationUpdatePayload;
            scope: string;
            correlationId: string;
          },
        ) => adapter.markAsPending(state),
        removeOnePessimistic: (state, payload: { data: number; jobTemplateId: number }) =>
          defaultActions.removeOnePessimistic(state, payload),
        duplicate: (
          state,
          payload: {
            data: number;
            additionalData: WorkflowAutomationAdditionalData;
            scope: string;
          },
        ) => adapter.markAsPending(state),
        duplicateSuccess: (state, payload: { data: WorkflowAutomation; scope: string }) =>
          pipeState(
            state,
            next => adapter.addOne(next, payload.data, { scope: payload.scope }),
            next => adapter.markAsLoaded(next),
          ),
        duplicateFailed: defaultActions.createOnePessimisticFailed,
        fetchOptions: ngrxEvent<{ id: number }>(),
        fetchOptionsSuccess: ngrxEvent<WorkflowAutomationOptionsData>(),
        fetchOptionsFailed: ngrxEvent<{ id: number; error: HttpErrorResponse }>(),
        fetchAutomationTemplates: ngrxEvent<{ id: number }>(),
        fetchAutomationTemplatesSuccess: ngrxEvent<AutomationTemplate[]>(),
        fetchAutomationTemplatesFailed: ngrxEvent<{ id: number; error: HttpErrorResponse }>(),
      };
    },
  });

const JobTemplateWorkflowAutomationsActions = {
  fetchJobTemplateAutomations: actions.fetchJobTemplateAutomations,
  fetchJobTemplateAutomationsSuccess: actions.fetchJobTemplateAutomationsSuccess,
  fetchJobTemplateAutomationsFailed: actions.fetchJobTemplateAutomationsFailed,

  createOnePessimistic: actions.createOnePessimistic,
  createOnePessimisticSuccess: actions.createOnePessimisticSuccess,
  createOnePessimisticFailed: actions.createOnePessimisticFailed,

  updateOnePessimistic: actions.updateOnePessimistic,
  updateOnePessimisticSuccess: actions.updateOnePessimisticSuccess,
  updateOnePessimisticFailed: actions.updateOnePessimisticFailed,

  removeOnePessimistic: actions.removeOnePessimistic,
  removeOnePessimisticFailed: actions.removeOnePessimisticFailed,
  removeOnePessimisticSuccess: actions.removeOnePessimisticSuccess,

  duplicate: actions.duplicate,
  duplicateSuccess: actions.duplicateSuccess,
  duplicateFailed: actions.duplicateFailed,

  fetchOptions: actions.fetchOptions,
  fetchOptionsSuccess: actions.fetchOptionsSuccess,
  fetchOptionsFailed: actions.fetchOptionsFailed,

  fetchAutomationTemplates: actions.fetchAutomationTemplates,
  fetchAutomationTemplatesSuccess: actions.fetchAutomationTemplatesSuccess,
  fetchAutomationTemplatesFailed: actions.fetchAutomationTemplatesFailed,
};

export {
  JobTemplateWorkflowAutomationsActions,
  namespace as JobTemplateWorkflowAutomationsNamespace,
  reducer as JobTemplateWorkflowAutomationsReducer,
  selectors as JobTemplateWorkflowAutomationsSelectors,
};
